import { computed, ref } from 'vue';
import { defineStore } from 'pinia';
import isNull from 'lodash/isNull';
import service from '@/services';
import { DEVICE_CATEGORY } from '@/config/constants';
import { setAbortController } from '@/composables/useAbortController';
import { downloadFile } from '@/utils/blob.js';
import { filterDeviceIds } from '@/utils/device-filter';
import { useSearchStore } from '@/stores/searchStore';
import { useTrackersListStatusStore } from '@/stores/useTrackersListStatusStore';
import { useToast } from 'vue-toastification';

export const useDevice = defineStore('device', () => {
  const deviceView = ref({});
  const devices = ref(new Map());
  const bleDevices = ref(new Map());
  const isLoading = ref(true);

  const filter = ref({
    specificIds: null,
    haveNotReportedDays: null,
    battery: null,
    missedUpdate: null,
    includeCellfixes: null,
    haveMovedDays: null,
    haveNotMovedDays: null,
  });

  const shouldRollbackZoom = ref(false);
  const resultDeviceIds = ref([]);
  const deviceIdsFromMap = ref([]);
  const foundDeviceIds = ref([]);
  const selectedIds = ref([]);

  const activeTab = ref(DEVICE_CATEGORY.allTrackers);
  const mobileActiveTab = ref('right');

  const isAtLeastOneFilterApplied = computed(() => {
    const f = filter.value;
    return [
      f.specificIds,
      f.haveNotReportedDays,
      f.battery,
      f.missedUpdate,
      f.includeCellfixes,
      f.haveMovedDays,
      f.haveNotMovedDays,
    ].some((field) => !isNull(field));
  });
  const showingSpecificIds = computed(() => filter.value.specificIds);

  const setFilter = (value) => {
    filter.value = value;
  };

  const setMobileActiveTab = (tab: string) => {
    mobileActiveTab.value = tab;
  };

  const setActiveTab = (tab: string) => {
    activeTab.value = tab;
  };

  const setSelectedIds = (ids) => {
    selectedIds.value = ids;
  };

  const fillStoreDevicesFromResponse = (
    mapDevices: Map<number, any>,
    rawDevices?: any,
    isReset: boolean = false,
  ) => {
    if (isReset) {
      mapDevices.clear();
    }
    rawDevices = rawDevices || [];
    for (const device of rawDevices) {
      // Create a shallow copy first, then deep copy nested objects to avoid mutating readonly objects from vue-query
      const deviceCopy = {
        ...device,
        // Deep copy nested objects
        subscription: device.subscription ? { ...device.subscription } : undefined,
        model: device.model ? { ...device.model } : undefined,
      };
      // @todo delete after refactoring api
      // Note: current_position is handled separately via usePosition store
      delete deviceCopy.current_position;
      // delete deviceCopy.group_ids;
      mapDevices.set(deviceCopy.id, deviceCopy);
    }

    return mapDevices;
  };

  const fetchByAccountId = async (accountId: number, params: any = {}) => {
    isLoading.value = true;

    const statusStore = useTrackersListStatusStore();
    const selectedStatuses = statusStore.getSelectedStatuses('trackers');
    const deviceStatus =
      params.device_status ??
      (selectedStatuses?.length ? selectedStatuses.join(',') : 'active');
    const requestParams = {
      exclude_groups: true,
      ...params,
      device_status: deviceStatus,
    };

    const controller = setAbortController('device');
    const response = await service.get(`/accounts/${accountId}/device-list`, {
      params: requestParams,
      signal: controller.signal,
    });

    if (response && response.status === 499) {
      isLoading.value = false;
      return response;
    }

    fillStoreDevicesFromResponse(devices.value, response.data.data);

    isLoading.value = false;
    return response;
  };

  const fetchBleSupportedDevices = async (accountId: number) => {
    isLoading.value = true;
    const response = await service.get(`/accounts/${accountId}/device-list`, {
      params: { ble_support: true, device_status: 'active,provisioned', exclude_groups: true },
    });

    fillStoreDevicesFromResponse(bleDevices.value, response.data.data);
    isLoading.value = false;
  };

  const fetchByAccountIdAndId = async (
    accountId: number,
    deviceId: number,
    params?: any,
  ) => {
    isLoading.value = true;
    const response = await service.get(
      `/accounts/${accountId}/devices/${deviceId}`,
      {
        params,
      },
    );

    const device = response?.data?.data;
    deviceView.value = device;
    if (device?.id) {
      devices.value.set(device.id, device);
    }
    isLoading.value = false;
    return device;
  };

  const fetchByCurrentAccountId = async (accountId: number, params: any) => {
    isLoading.value = true;
    const response = await service.get(`/accounts/${accountId}/devices`, {
      params,
    });

    fillStoreDevicesFromResponse(devices.value, response.data.data);

    isLoading.value = false;
    return response;
  };

  const fetchIdsByAccountIdAndParams = async (
    accountId: number,
    params: any,
  ): Promise<number[] | null> => {
    const controller = setAbortController('device');
    const response = await service.post(
      `/accounts/${accountId}/device-ids`,
      params,
      {
        signal: controller.signal,
      },
    );

    return response.data?.data;
  };

  // A blob download surfaces its error as a JSON blob body. On network/timeout
  // failures there is no response at all, so guard the parse and fall back to a
  // generic message rather than throwing out of the catch block.
  const blobErrorMessage = async (e: any) => {
    try {
      const text = await e?.response?.data?.text?.();
      return (text && JSON.parse(text)?.message) || 'An error occurred';
    } catch {
      return 'An error occurred';
    }
  };

  const downloadByAccountId = async (fileName: string, params: any) => {
    try {
      const response = await service.post(
        '/report-builder/',
        {
          type: 'positions',
          response_type: 'csv',
          ...params,
        },
        {
          responseType: 'blob',
        },
      );

      return downloadFile({ res: response, fileName, extension: 'csv' });
    } catch (e) {
      useToast().error(await blobErrorMessage(e));
    }
  };

  const downloadReportPositions = async (params: any) => {
    const response = await service.post('/report-builder/', params);

    return response.data.data;
  };

  // Export the selected trackers as a CSV via the device-list endpoint.
  // device_ids is comma-joined so it rides as a single query param.
  const exportDeviceList = async (
    accountId: number,
    deviceIds: number[],
    fileName: string,
  ) => {
    try {
      const response = await service.post(
        `/accounts/${accountId}/device-list`,
        null,
        {
          params: { download: true, device_ids: deviceIds.join(',') },
          responseType: 'blob',
        },
      );

      return downloadFile({ res: response, fileName, extension: 'csv' });
    } catch (e) {
      useToast().error(await blobErrorMessage(e));
    }
  };

  const fetchTriggersById = async (id: number) => {
    const response = await service.get(`/devices/${id}/triggers`);
    return response?.data.data;
  };

  const updateTriggersById = async (payload: any) => {
    const response = await service.post(
      `/devices/${payload.device_id}/triggers`,
      payload,
    );
    return response?.data.data;
  };

  /**
   * Check which selected devices can be unarchived (within 10-day recovery).
   * Returns { restorable: number[], not_restorable: { id: number, reason: string }[] }.
   */
  const checkUnarchiveEligibility = async (
    accountId: number,
    deviceIds: number[],
  ): Promise<{ restorable: number[]; not_restorable: { id: number; reason: string }[] }> => {
    const response = await service.post(
      `/accounts/${accountId}/devices/visibility-check`,
      { device_ids: deviceIds },
    );
    return response.data;
  };

  /**
   * Set device visibility (archive/unarchive). status: 'hidden' | 'active'.
   * Resolves on 2xx or on 422 when at least one device was updated (partial success).
   */
  const updateDevicesVisibility = async (
    accountId: number,
    deviceIds: number[],
    status: 'hidden' | 'active',
  ) => {
    try {
      const response = await service.post(
        `/accounts/${accountId}/devices/visibility`,
        { device_ids: deviceIds, status },
      );
      return response.data;
    } catch (error: any) {
      const status = error?.response?.status;
      const data = error?.response?.data;
      if (status === 422 && data?.updated?.length > 0) {
        return data;
      }
      throw error;
    }
  };

  const applyFilter = (newFilter?: any) => {
    shouldRollbackZoom.value =
      filter.value.specificIds?.length > 0 && !newFilter?.value?.specificIds;
    if (newFilter?.value) {
      filter.value = { ...filter.value, ...newFilter.value };
    }

    const searchStore = useSearchStore();
    const statusStore = useTrackersListStatusStore();

    resultDeviceIds.value = filterDeviceIds(
      Array.from(devices.value.keys()),
      devices.value,
      {
        search: searchStore.normalizedQuery,
        filter: filter.value,
        selectedStatuses: statusStore.getSelectedStatuses('trackers'),
      },
    );
  };

  const backupDevicesFromMap = (deviceIds) => {
    deviceIdsFromMap.value = deviceIds;
  };

  const getById = (id: number) => {
    return devices.value.get(id);
  };

  const addDevice = (device: any) => {
    return devices.value.set(device.id, device);
  };

  return {
    devices,
    bleDevices,
    deviceView,
    resultDeviceIds,
    foundDeviceIds,
    deviceIdsFromMap,
    selectedIds,
    filter,
    activeTab,
    mobileActiveTab,
    shouldRollbackZoom,

    isAtLeastOneFilterApplied,
    isLoading,
    showingSpecificIds,

    applyFilter,
    setFilter,
    setMobileActiveTab,
    setActiveTab,
    setSelectedIds,
    fetchByAccountId,
    fetchByAccountIdAndId,
    fetchByCurrentAccountId,
    fetchBleSupportedDevices,
    fetchIdsByAccountIdAndParams,
    fetchTriggersById,
    updateTriggersById,
    checkUnarchiveEligibility,
    updateDevicesVisibility,
    downloadByAccountId,
    downloadReportPositions,
    exportDeviceList,
    fillStoreDevicesFromResponse,
    backupDevicesFromMap,
    getById,
    addDevice,

    clearResultDeviceIds: () => (resultDeviceIds.value = []),
  };
});
